import Scene = Phaser.Scene;
import {ImageButton} from "@/scenes.ui/image-button";
import {Creature} from "@/store/level/types";
import CreaturePanel from "@/scenes.ui/creature-panel";
import {Keys} from "@/constants/key";
import {Player} from "@/store/player/types";
import {CreatureUtils} from "@/utils/creature-utils";
import Image = Phaser.GameObjects.Image;
import {CreaturesNavPanel} from "@/scenes.ui/creatures-nav-panel";
import SoundUtils from "@/utils/sound-utils";
import PlayerStore from "@/store/player";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";

export default class MiniCreaturesPanel extends Phaser.GameObjects.Container implements CreaturesNavPanel {

    private canvas: HTMLCanvasElement | undefined;

    private creatureIndex: number = 0;
    private selectCallback: ((creature: Creature) => unknown) | undefined = undefined;

    private creaturePanelById: Map<number, CreaturePanel> = new Map<number, CreaturePanel>();

    private selectionTop: Image | undefined;
    private selectionBottom: Image | undefined;

    private selectSound!: Phaser.Sound.BaseSound | undefined;

    private creatures!: Creature[];

    constructor(scene: Scene, canvas: HTMLCanvasElement, x: number, y: number, selectCallback: (creature: Creature) => unknown,
                creaturesList: Creature[], creatureIndex: number) {
        super(scene);

        this.scene = scene;
        this.canvas = canvas;
        this.selectCallback = selectCallback;
        this.creatureIndex = creatureIndex >= 0? creaturesList.length - 1 - creatureIndex : 0;
        this.creatures = creaturesList;
        this.x = x * CU.S;
        this.y = y + 100;
        this.setDepth(1000);

        this.selectSound = SoundUtils.addSound(scene, Keys.SOUND_SELECT);

        const size = this.creatures.length;

        for (let c of this.creatures) {
            this.addCreaturePanel(c, size);
        }

        let offset = this.getArrowOffset(size);
        const leftButton = SpriteUtils.scale(new ImageButton(this.scene, -offset, 30, Keys.ATLAS_PROFILE, Keys.UI_ARROW_LEFT), size <= 7 ? 1 / CU.S : 0.6 / CU.S);
        const rightButton = SpriteUtils.scale(new ImageButton(this.scene, offset, 30, Keys.ATLAS_PROFILE, Keys.UI_ARROW_RIGHT), size <= 7 ? 1 / CU.S : 0.6 / CU.S);
        this.add(leftButton);
        this.add(rightButton);

        this.initButton(leftButton, (creatures: Creature[]) => {
            --this.creatureIndex;
            if (this.creatureIndex < 0) {
                this.creatureIndex = creatures!.length - 1;
            }
        });
        this.initButton(rightButton, (creatures: Creature[]) => {
            ++this.creatureIndex;
            if (this.creatureIndex >= creatures!.length) {
                this.creatureIndex = 0;
            }
        });

        let c = this.creatures[this.creatureIndex];
        let cp = this.creaturePanelById.get(c.id);
        this.selectionTop = SpriteUtils.addImage(this.scene, cp!.x / CU.S, -24, Keys.ATLAS_UI, Keys.UI_ARROW_UP).setInteractive();
        this.selectionBottom = SpriteUtils.addImage(this.scene, cp!.x / CU.S, 95, Keys.ATLAS_UI, Keys.UI_ARROW_DOWN).setInteractive();

        this.add([this.selectionTop, this.selectionBottom]);
    }

    public refreshSkills(): void {
        const self = this;
        const player: Player = PlayerStore.getPlayer();

        player.creatures.forEach(c => {
            let cp: CreaturePanel | undefined = self.creaturePanelById.get(c.id);
            if (cp) {
                cp.setSkills(c.skills);
                cp.setHealth(CreatureUtils.getUnitHealth(c));
            }
        });
    }

    public select(creatureIndex: number) {
        this.creatureIndex = this.creatures.length - 1 - creatureIndex;
        let creature: Creature = this.creatures[this.creatureIndex];
        this.doSelect(creature);
    }

    public updateCreaturePanel(creatures: Creature[]): void {
        let self = this;
        creatures.forEach(c => {
            let cp: CreaturePanel | undefined = self.creaturePanelById.get(c.id);
            if (cp) {
                cp.setPanelCombative(c.combative);
            }
        });
    }

    private addCreaturePanel(c: Creature, size: number): void {
        const self = this;
        const cellWidth = this.getCellWidth(size);
        let cntr = this.creaturePanelById.size;
        let startX = cellWidth / 2 - size * cellWidth / 2;
        let creaturePanel = new CreaturePanel(this.scene!, startX + cntr * cellWidth, 30, c,
            () => {}, true);
        creaturePanel.setDepth(1000);
        SpriteUtils.scale(creaturePanel, this.getScale(size));
        this.add(creaturePanel);

        creaturePanel.setCallback(() => {
            self.creatureIndex = self.creatures.indexOf(c);
            self.doSelect(c);
        });
        creaturePanel.setHealthVisible();

        this.creaturePanelById.set(c.id, creaturePanel);
    }

    private getCellWidth(size: number) {
        if (size <= 3) {
            return 100;
        } else if (size <= 6) {
            return 80;
        } else if (size <= 8) {
            return 70;
        }
        return 60;
    }

    private getScale(size: number) {
        return 0.7 / CU.S;
    }

    private getArrowOffset(size: number) {
        if (size <= 3) {
            return 220;
        } else if (size <= 6) {
            return 270;
        } else if (size <= 7) {
            return 282;
        }
        return 290;
    }

    public doSelect(creature: Creature) {
        SoundUtils.play(this.selectSound);

        this.scene.tweens.add({
            targets: this.selectionTop,
            duration: 500,
            props: {
                x: { value: this.creaturePanelById.get(creature.id)!.x, ease: 'Quint.easeOut'}
            },
        });
        this.scene.tweens.add({
            targets: this.selectionBottom,
            duration: 500,
            props: {
                x: { value: this.creaturePanelById.get(creature.id)!.x, ease: 'Quint.easeOut'}
            },
        });

        this.selectCallback!(creature);
    }

    private initButton(button: ImageButton, changeIndex: (creatures: Creature[]) => unknown) {
        const self = this;
        button.setCallback(() => {
            if (this.creatures) {
                changeIndex(this.creatures);
                let creature: Creature = this.creatures[this.creatureIndex];
                self.doSelect(creature)
            }
        });
    }

    public setOrigin(x: number, y: number): void {
        // do nothing
    }

    getActive(): boolean {
        return this.active;
    }
    getGameObject(): Phaser.GameObjects.GameObject {
        return this;
    }
}
